import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import ExemptSubMenu from '@/components/nav/ExemptSubMenu';
import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import Popup990T from '@/components/ui/Form990TPopup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import { getLastDateOfTheMonth } from '@/utils/utils';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const Form990T = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [removelistener, setRemovelistener] = useState(true);
  const [tempReturn, setTempReturn] = useState(false);

  // Define an array of imported images
  const images = [
    '/assets/images/2290-steps-img.webp',
    '/assets/images/easy-steps-img.webp',
    '/assets/images/941-steps-img.webp',
    // Add more image URLs as needed
  ];
  const Ref = React.useRef<HTMLDivElement>(null);

  // Handle scrolling to change images
  const handleScroll = (event: any) => {
    const delta = event.deltaY;
    if (delta > 0) {
      // Scroll down, show the next imag
      if (currentImageIndex < 2) {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        setRemovelistener(false);
      } else {
        setRemovelistener(true);
      }
    } else if (delta < 0) {
      // Scroll up, show the previous image
      if (currentImageIndex > 0) {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
        setRemovelistener(false);
      } else {
        setRemovelistener(true);
      }
    } else {
      setRemovelistener(false);
    }
  };
  function useHorizontalScroll() {
    useEffect(() => {
      const el = Ref.current;
      setTempReturn(tempReturn);
      if (el) {
        const onWheel = (e: any) => {
          if (e.deltaY === 0) return;
          e.preventDefault();
          el.scrollTo({
            behavior: 'smooth',
            top: 0,
            left: 0,
          });
        };

        el.addEventListener('wheel', onWheel);
        if (removelistener === false) {
          el.addEventListener('wheel', onWheel);
        } else if (removelistener === true) {
          el.removeEventListener('wheel', onWheel);
        } else {
          el.addEventListener('wheel', onWheel);
        }
        return () => {
          el.removeEventListener('wheel', onWheel);
        };
      }
      return () => setTempReturn(false);
    }, [removelistener]);
    return Ref;
  }

  const scrollRef = useHorizontalScroll();

  const [open, setOpen] = useState(false);
  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Simplify your nonprofit's tax return with Form 990-EZ.`,
  //   SubHeading:
  //     'E-file now for a seamless experience. Stay tax-exempt and file confidently.',
  //   CtaText: 'E-file Form 990-EZ Now',
  //   CtaSubHeading: `Stay tax compliant with the IRS`,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=sticky`,
  //   isActive: true,
  // };

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 5, 15));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 5, 15)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);
  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
            color: '#fff',
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            color: '#fff',
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
            color: '#d7d7d7',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Day</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };

  return (
    <AppLayout
      //  LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="E-File Form 990-T | Simplify Tax Filing for Exempt Orgs"
          description="Simplify Form 990-T with TaxZerone's secure e-filing platform. File your unrelated business income tax return quickly and accurately. Get started today!"
          keywords=""
          canonical="tax-exempt/e-file-irs-form-990-t/"
          jsonLdPricing=""
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2183"
          ogShareImg="assets/images/exempt-share-img.png"
        />
      }
    >
      <ExemptSubMenu />
      <Popup990T />
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Container
        component="section"
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #c02652, #213e99)',
            p: 1.5,
            px: 2.5,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: 16, sm: 17, md: 22, lg: '1.25rem' },
                color: '#ccc',
                lineHeight: 1.3,
              }}
            >
              <strong>Attention Tax-Exempt Organizations! </strong> E-file your{' '}
              <b style={{ color: '#fff', fontWeight: 600 }}>
                {' '}
                2025 Form 990-T{' '}
              </b>{' '}
              today to report unrelated business income and stay compliant.
            </Typography>
            <Box>
              {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time Left Until the Deadline
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  ( May 15, 2026)
                </span>
                :
              </Typography> */}
              <Countdown date={lastDayTaxSeason} renderer={renderer} />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
                sx={{
                  backgroundColor: '#fff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                    boxShadow: 'none',
                  },
                  px: 2,
                  color: '#000',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                E-File Now!
              </Button>
              {/* <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 1, color: '#fff' }}
              >
                {' '}
                <b> Save 10%</b> with code{' '}
                <b style={{ color: '#80ffdb' }}> TZOTE10</b>
              </Typography> */}
            </Box>
          </Stack>
        </Box>
      </Container>
      <Container maxWidth="xl" component="section">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                E-File IRS{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 990-T</span>
                </span>{' '}
                for{' '}
                <Hidden smDown>
                  {' '}
                  <br />
                </Hidden>{' '}
                the 2025 Tax Year
              </Typography>
              <Box
                component="ul"
                sx={{
                  mb: 2,
                  '& li': {
                    mb: 1.2,
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  },
                }}
              >
                <li>
                  Effortlessly report your nonprofit’s unrelated business income
                  (UBI) with TaxZerone.{' '}
                </li>
                <li>
                  Additional forms such as{' '}
                  <span style={{ fontWeight: 600 }}>
                    3800, 3468, 8911, and 8936
                  </span>{' '}
                  are included.
                </li>
                <li>
                  TaxZerone supports tax years{' '}
                  <span style={{ fontWeight: 600 }}>2025, and 2024</span>.{' '}
                </li>
              </Box>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box
                  sx={{
                    textAlign: 'center',
                    mr: { xs: 0, md: 4 },
                    mb: { xs: 2, sm: 0 },
                  }}
                >
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
                  >
                    E-File Form 990-T
                  </Button>
                  {/* <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    Takes 3 steps and less than 5 minutes
                  </Typography> */}
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/990-t-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/990-t-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/990-t-landing-img.png"
                  alt="E-File IRS Form 990-T with TaxZerone"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/990-t-landing-img.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/990-t-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/990-t-landing-img-sm.png"
                  alt="E-File IRS Form 990-T with TaxZerone"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 30, sm: 26, xs: 22 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.5,
                }}
              >
                File Form 990-T online for{' '}
                <Box
                  component="strong"
                  sx={{
                    color: '#ffe400',
                    fontSize: { xs: 30, sm: 36, md: 48, lg: 56 },
                  }}
                >
                  $129.99
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 16, md: 16, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                }}
              >
                TaxZerone helps you file your unrelated business income tax with
                accuracy, convenience, and reliability.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 250, md: 280 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              <Button
                size="large"
                variant="contained"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
                sx={{
                  boxShadow: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  color: '#0a7581',
                  '&:hover, &:focus, &:active': {
                    color: '#fff',
                    backgroundColor: '#172b6b',
                  },
                }}
              >
                E-File Now
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 0, mb: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                No additional or hidden cost for supporting schedules.
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#ffffff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/why-do-nonprofit-file-form-990-t-img.webp" />
                  <source srcSet="/assets/images/why-do-nonprofit-file-form-990-t-img.png" />
                  <img
                    src="/assets/images/what-is-form-990-img.png"
                    alt="Why do nonprofits file Form 990-T?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={500}
                    height={550}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Why do nonprofits file Form 990-T?
              </Typography>

              <List
                sx={{
                  display: 'inline-block',
                  mb: 2,
                  '& .MuiListItemText-primary': {
                    fontSize: { lg: 15, md: 14, sm: 13.5, xs: 13 },
                  },
                }}
              >
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <span style={{ fontWeight: 600 }}>IRS Form 990-T</span> is a
                    tax return used by tax-exempt organizations to report{' '}
                    <span style={{ fontWeight: 600 }}>
                      unrelated business income (UBI).
                    </span>
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    It applies when a nonprofit earns income from activities{' '}
                    <span style={{ fontWeight: 600 }}>
                      not directly related to its mission.
                    </span>
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    Even though the organization is tax-exempt, this income may
                    still be{' '}
                    <span style={{ fontWeight: 600 }}>subject to tax.</span>
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    The form ensures organizations{' '}
                    <span style={{ fontWeight: 600 }}>
                      pay taxes on non-exempt business activities.
                    </span>
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    It is separate from annual filings like{' '}
                    <span style={{ fontWeight: 600 }}>
                      <Link
                        href="/tax-exempt/e-file-irs-form-990/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990
                      </Link>
                      ,{' '}
                      <Link
                        href="/tax-exempt/e-file-irs-form-990-ez/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-EZ
                      </Link>
                      , or{' '}
                      <Link
                        href="https://epostcard.taxzerone.com/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-N
                      </Link>
                      .
                    </span>
                  </ListItemText>
                </ListItem>
              </List>

              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                  mb: 0.5,
                  fontWeight: 600,
                }}
              >
                Deadline:
              </Typography>

              <List
                sx={{
                  display: 'inline-block',
                  mb: 2,
                }}
              >
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    Most tax-exempt organizations must file Form 990-T by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th day of the 5th month
                    </span>{' '}
                    after their tax year ends (typically{' '}
                    <span style={{ fontWeight: 600 }}>May 15</span> for
                    calendar-year filers).
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    IRAs, Roth IRAs, and certain employee trusts must file by
                    the{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th day of the 4th month
                    </span>{' '}
                    after the tax year ends.
                  </ListItemText>
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                When is Form 990-T required?
              </Typography>

              <List
                sx={{
                  display: 'inline-block',
                  mb: 2,
                }}
              >
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    If your tax-exempt organization brought in{' '}
                    <span style={{ fontWeight: 600 }}>
                      $1,000 or more in gross unrelated business income,
                    </span>{' '}
                    you need to file.
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <span style={{ fontWeight: 600 }}>
                      501(c)(3) nonprofits
                    </span>{' '}
                    – charities, foundations, colleges, and religious
                    organizations.
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <span style={{ fontWeight: 600 }}>
                      Other 501(a) exempt organizations
                    </span>{' '}
                    – trade associations, social clubs, and labor unions.
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <span style={{ fontWeight: 600 }}>
                      IRAs and retirement accounts
                    </span>{' '}
                    – if certain investments generate unrelated business income.
                  </ListItemText>
                </ListItem>

                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 'inherit', mr: 2, mt: -2.3 }}>
                    <CheckCircleIcon sx={{ color: '#1da94a' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <span style={{ fontWeight: 600 }}>Trusts and LLCs</span> –
                    those treated as tax-exempt under IRS rules may also have a
                    filing obligation.
                  </ListItemText>
                </ListItem>
              </List>

              <Alert
                severity="warning"
                icon={false}
                sx={{
                  mb: 4,
                  textAlign: 'left',
                  borderLeft: '3px solid rgb(102, 60, 0)',
                }}
              >
                <span style={{ fontWeight: 600 }}>Important:</span>{' '}
                <Box
                  component="ul"
                  sx={{
                    mb: 1,
                    ml: 3.5,
                    pl: 0,
                    '& li': {
                      mb: 1.8,
                      listStyle: 'none',
                      position: 'relative',
                      pl: 3.5,
                      textAlign: 'left',
                      '&::before': {
                        content: '"✔"',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        color: '#1da94a',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        lineHeight: 1.4,
                      },
                    },
                  }}
                >
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>$1,000 threshold</span> is
                    based on{' '}
                    <span style={{ fontWeight: 600 }}>
                      gross income, not profit.
                    </span>
                  </li>

                  <li>
                    Missing this filing may result in penalties.{' '}
                    <span style={{ fontWeight: 600 }}>
                      Learn more about <br />
                      <Link
                        href="/resources/tax-exempt/form-990-penalties/"
                        sx={{ color: '#0202ff' }}
                      >
                        990 penalties
                      </Link>{' '}
                      on our instructions page.
                    </span>
                  </li>
                </Box>
              </Alert>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/when-is-from-990-t-required-img.webp" />
                  <source srcSet="/assets/images/when-is-from-990-t-required-img.png" />
                  <img
                    src="/assets/images/who-file-f990-img.png"
                    alt=" When is Form 990-T required?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={500}
                    height={550}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Choose TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Check out the features that make TaxZerone an ideal choice for
                exempt organizations to e-file 990-T returns.
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={2}
            mt={4}
            pb={{ xs: 0, sm: 4, md: 7 }}
            justifyContent="center"
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/instant-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/instant-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/instant-icon.png"
                      alt="Seamless Status Tracking"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Seamless Status Tracking
                </Typography>
                <Typography variant="body1">
                  Know exactly where your filing stands. Once submitted,
                  TaxZerone informs you with real-time updates on your
                  return&apos;s status. No more waiting for your tax-exempt
                  status!
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="IRS-authorized"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  IRS-authorized
                </Typography>
                <Typography variant="body1">
                  File with confidence. TaxZerone is an IRS-authorized e-filing
                  provider, ensuring your 990-T is seamlessly accepted by the
                  IRS
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="Guided Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Guided Filing
                </Typography>
                <Typography variant="body1">
                  Access step-by-step instructions to complete filing quickly.
                  You can also access our help articles to understand Form 990-T
                  return better.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/paypal-icon.webp" />
                    <source srcSet="/assets/images/paypal-icon.png" />
                    <img
                      src="/assets/images/paypal-icon.png"
                      alt="Pay Securely with PayPal "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Pay Securely with PayPal
                </Typography>
                <Typography variant="body1">
                  Complete your filing with confidence using{' '}
                  <span style={{ fontWeight: 600 }}>PayPal</span>. It&apos;s
                  fast, secure, and offers added payment flexibility—no need to
                  enter card details directly.
                </Typography>
              </Paper>
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 6,md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/click-icon.webp" />
                    <source srcSet="/assets/images/click-icon.png" />
                    <img
                      src="/assets/images/click-icon.png"
                      alt="Click2File for Effortless Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Click2File for Effortless Filing
                </Typography>
                <Typography variant="body1">
                  With <span style={{ fontWeight: 600 }}>Click2File</span>, we
                  automatically transfer relevant data from your previous return
                  to your current one, making filing even easier and faster.
                </Typography>
              </Paper>
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/secure-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/secure-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Unparalleled Security"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Unparalleled Security
                </Typography>
                <Typography variant="body1">
                  Your privacy is paramount. TaxZerone utilizes advanced
                  security measures to safeguard your information throughout the
                  e-filing process. Rest assured, your 990-T is submitted
                  securely to the IRS.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/free-efile-retransmission-icon.webp" />
                    <source srcSet="/assets/images/free-efile-retransmission-icon.png" />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Retransmission "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Retransmission
                </Typography>
                <Typography variant="body1">
                  If the IRS rejects your 990-T due to any errors, you can
                  correct the issues and retransmit the return{' '}
                  <span style={{ fontWeight: 600 }}>
                    without any additional fee
                  </span>
                  .
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                  //  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/easy-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/easy-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/easy-icon.png"
                      alt="Effortless Navigation"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Effortless Navigation
                </Typography>
                <Typography variant="body1">
                  TaxZerone&apos;s intuitive interface lets you complete your
                  entire 990-T e-filing in minutes. Simply answer a series of
                  clear questions, and our software will handle the rest.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/calculate-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/calculate-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/calculate-icon.png"
                      alt="Accuracy and Compliance"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Accuracy and Compliance
                </Typography>
                <Typography variant="body1">
                  Built-in IRS validation checks are integrated seamlessly
                  within our platform. These proactive measures minimize errors,
                  ensuring your 990-T is filed accurately, and minimizing the
                  chances of IRS rejections.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box
            component="div"
            sx={{
              margin: 'auto',
              textAlign: 'center',
              mt: 4,
              mb: { xs: 0, md: 0 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 21, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                lineHeight: 1.5,
                mb: 3,
              }}
            >
              TaxZerone supports filing for{' '}
              <span style={{ fontWeight: 600 }}> multiple tax years</span>,
              helping you efficiently manage past and current returns.
            </Typography>
            <Button
              variant="contained"
              sx={{
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
              }}
              className="btn-primary medium"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
            >
              E-File &nbsp;<b>Form 990-T</b>&nbsp;Now
            </Button>
            <Typography variant="body2" sx={{ mt: 1, mb: { xs: 2, md: 0 } }}>
              Simplified e-filing process
            </Typography>
          </Box>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Form 990-T e-filing made simple with TaxZerone
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Follow the steps below to file Form 990-T for your exempt
                organization.
              </Typography>
            </Grid>
          </Grid>
        </Container>

        <Container
          maxWidth="xl"
          sx={{ mb: 5 }}
          ref={scrollRef}
          className="image-gallery"
          onWheel={handleScroll}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source srcSet="/assets/images/990-t-steps-img.webp" />
                <source srcSet="/assets/images/990-t-steps-img.png" />
                <img
                  src="/assets/images/990-t-steps-img.png"
                  alt="Follow the steps below to file Form 990-T for your private foundation."
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
              {/* <img
                src={images[currentImageIndex]}
                alt={`Image ${currentImageIndex + 1}`}
                className="imagescroll"
                loading="lazy"
              /> */}
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Provide Organization Details"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Provide Organization Details
                    </Typography>
                    <Typography variant="body1">
                      Choose the tax year you want to file a return and provide
                      your organization’s details.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Preview the return"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Preview the return
                    </Typography>
                    <Typography variant="body1">
                      Review the information provided in the return for accuracy
                      before transmitting.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Transmit to the IRS"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Transmit to the IRS
                    </Typography>
                    <Typography variant="body1">
                      Transmit your 990-T return to the IRS and get the
                      acceptance in just a few hours.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>

              <Box
                sx={{
                  textAlign: { xs: 'center', md: 'center' },
                  maxWidth: 300,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
                >
                  E-File Form 990-T
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: { xs: 2, sm: 2, md: 5 } }}
                >
                  Stay tax compliant with the IRS
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            component="section"
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 5 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Supporting Forms for 990-T
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 2,
                  }}
                >
                  <b>TaxZerone</b> currently supports the following 990-T
                  schedules and other related forms
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  They will be auto-generated while you file your 990-T form
                  with us.
                </Typography>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mb: 3, color: '#6e6e6e' }}
                >
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-990-t-schedule-a/"
                            sx={{ color: '#0202ff' }}
                          >
                            Schedule A
                          </Link>
                        </b>
                        &nbsp;- Unrelated Business Taxable Income From an
                        Unrelated Trade or Business
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/schedule-d-form-1120-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            1120 Schedule D
                          </Link>
                        </b>
                        &nbsp;- Capital Gains and Losses
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/schedule-d-form-1041-resource-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            1041 Schedule D
                          </Link>
                        </b>
                        &nbsp;- Capital Gains and Losses
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/1041-schedule-i-amt/"
                            sx={{ color: '#0202ff' }}
                          >
                            1041 Schedule I
                          </Link>
                        </b>
                        &nbsp;- Alternative Minimum Tax—Estates and Trusts
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-4797-sales-business-property/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 4797
                          </Link>
                        </b>
                        , Sales of Business Property
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-4562-depreciation-amortization-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 4562
                          </Link>
                        </b>
                        , Depreciation and Amortization
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-8949-sales-and-dispositions-guide/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8949
                          </Link>
                        </b>
                        , Sales and Other Dispositions of Capital Assets
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-8995-qualified-business-income-deduction/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8995
                          </Link>
                        </b>
                        , Qualified Business Income Deduction Simplified
                        Computation
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-2220-underpayment-estimated-tax-corporations/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 2220
                          </Link>
                        </b>
                        , Underpayment of Estimated Tax by Corporations
                      </Box>
                    </li>
                  </Box>
                </Typography>
                <Typography variant="body1">
                  {' '}
                  Get More Information on{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-T Instructions
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-t-schedule-a-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule A Instructions
                  </Link>{' '}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/schedules/990-t-sch-a-img.webp" />
                  <source srcSet="/assets/images/schedules/990-t-sch-a-img.png" />
                  <img
                    src="/assets/images/schedules/990-t-sch-a-img.png"
                    alt="Schedules and Forms for Form 990-T"
                    className="img-responsive img-shadow"
                    loading="lazy"
                    width={459}
                    height={420}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            component="section"
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid size={{ xs: 12, md: 6, sm: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'grid',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'left' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <Box>
                  <picture>
                    <source srcSet="/assets/images/form-990-t-credits-img.webp" />
                    <source srcSet="/assets/images/form-990-t-credits-img.png" />
                    <img
                      src="/assets/images/form-990-t-credits-img.png"
                      alt="Claiming Credits with Form 990-T"
                      className="img-responsive img-shadow"
                      loading="lazy"
                      width={429}
                      height={390}
                    />
                  </picture>
                  <Stack
                    spacing={2}
                    direction="column"
                    sx={{
                      mt: 2,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {' '}
                    <Box
                      component="iframe"
                      title="E-file Form 990-T & Claim Energy Credits (Form 3800 + 3468 Tutorial)"
                      src="https://www.youtube.com/embed/6CcuDX8gWT4?rel=0"
                      sx={{
                        width: { xs: '100%', sm: 400 },
                        height: { xs: '100%', sm: 230 },
                        border: '10px solid #333 !important',
                        borderRadius: 5,
                      }}
                      allowFullScreen={true}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: 'left',
                        mt: 0,
                        fontSize: 18,
                        fontWeight: 600,
                      }}
                    >
                      {' '}
                      Watch Tutorial
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6, sm: 12 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 3 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Claiming Credits with{' '}
                  <Hidden mdDown>
                    <br />
                  </Hidden>
                  Form 990-T
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 1,
                  }}
                >
                  When filing your Form 990-T, your exempt organization may be
                  eligible to claim various credits to reduce your tax
                  liability. To accurately claim these credits, you&apos;ll need
                  to complete and attach specific forms to your 990-T return.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  TaxZerone supports the following IRS forms, making it easy to
                  claim your eligible credits:
                </Typography>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mb: 3, color: '#6e6e6e' }}
                >
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-3800-business-credits/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 3800
                          </Link>{' '}
                          - General Business Credit
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for activities like research and
                        development, investment in certain properties, and
                        energy efficiency improvements.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-3468-investment-credit/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 3468
                          </Link>{' '}
                          - Investment Credit
                        </b>
                        <br /> Claim credits for investments in energy property,
                        new equipment, and qualified rehabilitation
                        expenditures.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-8911-alternative-fuel-credits/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8911
                          </Link>{' '}
                          - Alternative Fuel Vehicle Refueling Property Credit
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for investments in alternative fuel
                        vehicle refueling property, such as fueling stations and
                        dispensers.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          {' '}
                          <Link
                            href="/resources/tax-exempt/form-8936-clean-vehicle-credit/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8936
                          </Link>{' '}
                          - Clean Vehicle Credits
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for purchases of qualified plug-in
                        electric drive motor vehicles, fuel cell vehicles, and
                        certain motorcycles.
                      </Box>
                    </li>
                  </Box>
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{ px: { md: 18, sm: 4, xs: 2 }, textAlign: 'center', mb: 4 }}
          >
            <Typography variant="body1">
              Each of these forms covers a unique set of credits, making it easy
              to maximize your organization’s tax benefits. TaxZerone supports
              all these forms, ensuring a seamless experience as you file Form
              990-T with the necessary attachments.
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
              }}
              className="btn-primary"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
            >
              E-File Form 990-T
            </Button>
          </Box>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#ffffff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={7}
            mb={7}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={12}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'center',
                  md: 'center',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Simplify Form 990-T Filing with TaxZerone
              </Typography>

              <Box component="div" sx={{ mb: 4, maxWidth: 1100, m: 'auto' }}>
                <TableContainer sx={{ border: '1px solid #828282' }}>
                  <Table aria-label="TaxZerone Form 990-T Comparison">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#ccc' }}>
                        <TableCell
                          sx={{
                            color: '#444',
                            width: 350,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          Features
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            width: 350,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          TaxZerone
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            width: 350,
                            fontWeight: 600,
                            fontSize: { md: 18, sm: 16, xs: 16 },
                          }}
                        >
                          Other E-file Platforms
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          IRS-Authorized
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Real-Time Error Validation
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Built-in
                          IRS checks
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Limited
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Processing Time
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Within few
                          minutes
                        </TableCell>
                        <TableCell>1–2 Days</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Free Retransmission{' '}
                          <span style={{ fontWeight: 400 }}>(if rejected)</span>
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Additional
                          charges apply
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Ease of Use
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Beginner
                          friendly Guidance
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Limited
                          filing guidance
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Extra Fees or Hidden Costs
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> No Extra
                          Fees
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Extra fee
                          applies
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          SOC 2 Compliant
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes –{' '}
                          <Link href="#" sx={{ color: '#0202ff' }}>
                            Trust Center
                          </Link>
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Varies by
                          platform
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Data Import / Prior-Year Copy
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Supported
                          (Click2File Feature)
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Varies by
                          platform
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>IRS2File</TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#dc2626' }}>✘</span> Not
                          available
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Filing Fee Options
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Flexible
                          (Credit/debit card or PayPal)
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Card only
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          EIN Finder Tool
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Free tool
                          included
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#dc2626' }}>✘</span> Not
                          available
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          All Schedules Supported
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Yes - FREE
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#f59e0b' }}>⚠</span> Extra fee
                          per schedule
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Form 3800
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Included
                        </TableCell>
                        <TableCell>Varies based on providers</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Form 3468
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Included
                        </TableCell>
                        <TableCell>Varies based on providers</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Form 8911
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Included
                        </TableCell>
                        <TableCell>Varies based on providers</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Form 8936
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Included
                        </TableCell>
                        <TableCell>Varies based on providers</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Bulk Filing / Multiple Returns
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Excel
                          template import
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#dc2626' }}>✘</span> Not
                          available
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Automatic Calculations
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Built-in
                          calculations
                        </TableCell>
                        <TableCell>Partial calculation support</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Upload PDF{' '}
                          <span style={{ fontWeight: 400 }}>
                            (Full Service)
                          </span>
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Available
                        </TableCell>
                        <TableCell>Available on select platforms</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Instant Email Notifications
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Real-time
                          status updates
                        </TableCell>
                        <TableCell>Delayed notifications</TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: '#fff' }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Customer Support
                        </TableCell>
                        <TableCell>
                          <span style={{ color: '#16a34a' }}>✔</span> Tax
                          Experts Available
                          <br />
                          (via chat, e-mail, phone)
                        </TableCell>
                        <TableCell>Limited or outsourced</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent={{ xs: 'center', md: 'center' }}
              >
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-1"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-2"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-3"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-4"
                />
                <img
                  src="/assets/images/star-icon.svg"
                  width={30}
                  height={28}
                  alt="star-5"
                />
              </Stack>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                Hear What Our Clients Say About TaxZerone
              </Typography>

              <Grid
                container
                spacing={2}
                m={0}
                justifyContent="center"
                direction="row"
                className="MuiGrid-root-width"
                sx={{ mb: 3 }}
              >
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          TaxZerone is a game-changer. It made the whole 990-T
                          <br /> e-filing process so much easier and faster.
                          Highly recommended!{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          TaxZerone&apos;s user-friendly interface, top-notch
                          security, and timely updates in line with IRS
                          regulations make it our go-to choice.{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: '#fff',
                      p: 2,
                      borderRadius: 3,
                      minHeight: '100%',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ my: 1 }}
                      justifyContent={{ xs: 'center', md: 'center' }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &ldquo;
                      </Typography>
                      <Typography component="div" variant="body1" color="gray">
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: 16, sm: 18, md: 20 },
                            fontFamily: 'san-serif',
                            fontStyle: 'italic',
                            color: '#414042',
                            lineHeight: 1.2,
                          }}
                        >
                          Thanks to TaxZerone, filing taxes electronically has
                          become a breeze. Its built-in error detection ensures
                          our forms are error-free, and its support team is
                          exceptional!{' '}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 2 }}
                          justifyContent={{ xs: 'center', md: 'center' }}
                        >
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-1"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-2"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-3"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-4"
                          />
                          <img
                            src="/assets/images/star-icon.svg"
                            width={18}
                            height={17}
                            alt="star-5"
                          />
                        </Stack>
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: 'san-serif',
                          fontSize: { xs: 45, sm: 65 },
                          fontWeight: 600,
                          lineHeight: { xs: '30px', sm: '65px' },
                          color: '#999',
                        }}
                      >
                        &rdquo;
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        component="section"
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Stay Tax-Exempt Status with TaxZerone
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          It&apos;s time to simplify your e-filing process with TaxZerone!{' '}
          <br />
          E-file Form 990-T and keep enjoying the benefits of your tax-exempt
          status!
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            id="3800"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
          >
            E-File Form 990-T
          </Button>
        </Box>
      </Box>
      {/* <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          py: 5,
        }}
        spacing={0}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            className="MuiGrid-root-width"
            sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 3 }, mt: { xs: -3, md: 5 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Claiming Credits with{' '}
                  <Hidden mdDown>
                    <br />
                  </Hidden>
                  Form 990-T
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 1,
                  }}
                >
                  When filing your Form 990-T, your exempt organization may be
                  eligible to claim various credits to reduce your tax
                  liability. To accurately claim these credits, you&apos;ll need
                  to complete and attach specific forms to your 990-T return.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    color: '#333333',
                    mb: 3,
                  }}
                >
                  TaxZerone supports the following IRS forms, making it easy to
                  claim your eligible credits:
                </Typography>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mb: 3, color: '#6e6e6e' }}
                >
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-3800-business-credits/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 3800
                          </Link>{' '}
                          - General Business Credit
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for activities like research and
                        development, investment in certain properties, and
                        energy efficiency improvements.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-3468-investment-credit/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 3468
                          </Link>{' '}
                          - Investment Credit
                        </b>
                        <br /> Claim credits for investments in energy property,
                        new equipment, and qualified rehabilitation
                        expenditures.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          <Link
                            href="/resources/tax-exempt/form-8911-alternative-fuel-credits/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8911
                          </Link>{' '}
                          - Alternative Fuel Vehicle Refueling Property Credit
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for investments in alternative fuel
                        vehicle refueling property, such as fueling stations and
                        dispensers.
                      </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>
                        <b>
                          {' '}
                          <Link
                            href="/resources/tax-exempt/form-8936-clean-vehicle-credit/"
                            sx={{ color: '#0202ff' }}
                          >
                            Form 8936
                          </Link>{' '}
                          - Clean Vehicle Credits
                        </b>
                        <Hidden smDown>
                          <br />{' '}
                        </Hidden>{' '}
                        Claim credits for purchases of qualified plug-in
                        electric drive motor vehicles, fuel cell vehicles, and
                        certain motorcycles.
                      </Box>
                    </li>
                  </Box>
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
              >
                <picture>
                  <source srcSet="/assets/images/form-990-t-credits-img.webp" />
                  <source srcSet="/assets/images/form-990-t-credits-img.png" />
                  <img
                    src="/assets/images/form-990-t-credits-img.png"
                    alt="Claiming Credits with Form 990-T"
                    className="img-responsive img-shadow"
                    loading="lazy"
                    width={459}
                    height={420}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{ px: { md: 18, sm: 4, xs: 2 }, textAlign: 'center', mb: 4 }}
          >
            <Typography variant="body1">
              Each of these forms covers a unique set of credits, making it easy
              to maximize your organization’s tax benefits. TaxZerone supports
              all these forms, ensuring a seamless experience as you file Form
              990-T with the necessary attachments.
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                transition: 'all 0.3s ease 0s',
                '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
              }}
              className="btn-primary"
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form990t`}
            >
              E-File Form 990-T
            </Button>
          </Box>
        </Container>
      </Grid> */}
      <Container maxWidth="lg" component="section">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 5, md: 5 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            mt={{ xs: 2, sm: 5 }}
            mb={2}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions - Form 990-T
            </Typography>
            <Box
              component="div"
              sx={{ mb: 3 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. Who must file Form 990-T?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Any disregarded entity, a domestic, or foreign
                      organization exempt under Section 501(a), section 529(a),
                      or Section 529A(a) that files Form 990, 990-EZ, or 990-PF
                      and has a gross income of $1,000 or more from a regularly
                      conducted unrelated trade or business activities must file
                      Form 990-T.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How to extend the deadline of Form 990-T?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <Link
                        href="/resources/tax-exempt/form-990-due-dates/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        The deadline for Form 990-T
                      </Link>{' '}
                      can be extended easily by requesting an automatic 6-month
                      extension by submitting
                      <Link
                        href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 8868
                      </Link>{' '}
                      using TaxZerone. You can file the IRS Form 8868 before the
                      due date of Form 990-T.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How does Form 990 differ from Form 990-T?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box itemProp="text">
                    <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                      <li>
                        <Link
                          href="/tax-exempt/e-file-irs-form-990/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 990
                        </Link>{' '}
                        is filed by large organizations with gross receipts ≥
                        $200,000 or total assets ≥ $500,000 during the tax
                        year.{' '}
                      </li>
                      <li>
                        Form 990-T is a supporting form filed by nonprofits when
                        they have an unrelated business income of $1000 or more
                        in the tax year.{' '}
                      </li>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={12} textAlign="center" sx={{ mb: 2 }}>
            <Divider />
          </Grid>
          <Grid size={12} textAlign="center">
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
              }}
            >
              Related Resources
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                border: '1px solid #eaeaea',
                p: 1.5,
                backgroundColor: '#f0f2f9',
                pb: { xs: 2, sm: 2 },
                borderRadius: 3,
                minHeight: '100%',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: { xs: 16, md: 18 },
                  mb: { xs: 1, sm: 1 },
                  fontWeight: 600,
                }}
              >
                Form 990-T Instructions
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                }}
              >
                Read the{' '}
                <Link
                  href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/tax-exempt/form-990-t-instructions/`}
                  sx={{ color: '#0202ff' }}
                >
                  Instructions for Form 990-T
                </Link>{' '}
                to file accurately with TaxZerone.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                border: '1px solid #eaeaea',
                p: 1.5,
                backgroundColor: '#f0f2f9',
                pb: { xs: 2, sm: 2 },
                borderRadius: 3,
                minHeight: '100%',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: { xs: 16, md: 18 },
                  mb: { xs: 1, sm: 1 },
                  fontWeight: 600,
                }}
              >
                Form 990-T Due Date
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                }}
              >
                Know more about the{' '}
                <Link
                  href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/tax-exempt/form-990-due-dates/`}
                  sx={{ color: '#0202ff' }}
                >
                  Due Date of Form 990-T
                </Link>{' '}
                and file your form on time.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              sx={{
                border: '1px solid #eaeaea',
                p: 1.5,
                backgroundColor: '#f0f2f9',
                pb: { xs: 2, sm: 2 },
                borderRadius: 3,
                minHeight: '100%',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: { xs: 16, md: 18 },
                  mb: { xs: 1, sm: 1 },
                  fontWeight: 600,
                }}
              >
                Form 990-T Schedules
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                }}
              >
                Learn more about{' '}
                <Link
                  href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/tax-exempt/form-990-t-schedule-a/`}
                  sx={{ color: '#0202ff' }}
                >
                  Form 990-T Schedule A
                </Link>{' '}
                and file easily.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990T;
